import { useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import styled, { useTheme } from 'styled-components'
import {
  IngredientFeedCategoryTypeEnum,
  ProductIngredientScheme,
  ProductIngredientTypeEnum,
} from '@/apis/rest/generated/types'
import FlatIcon from '@/components/common/icons/FlatIcon'
import { BottomSheetModal } from '@/components/common/modals/BottomSheetModal'
import { Typo, TypoVariant } from '@/components/common/typography'
import { ScrollContainer } from '@/components/domains/etc'
import { ProductDetailIngredientsInfo } from '@/components/domains/product-detail/ProductDetailIngredientsInfo'
import { FEED_TEXT, INGREDIENT_TEXT, IngredientText } from '@/components/domains/product-detail/product-detail.type'
import { NewProductCard } from '@/components/domains/products/new-product-card/new-product-card'
import { PRODUCT_CARD_SIZES } from '@/components/domains/products/new-product-card/new-product-card.type'
import SkeletonProduct from '@/components/domains/products/skeleton/SkeletonProduct'
import { IconNameEnum } from '@/constants/icon-name.enum'
import { ProductThumbnailSizeEnum } from '@/constants/product-thumbnail-size.enum'
import { useProductDetailEventTracker } from '@/containers/event-tracker/product-detail.event-tracker-new'
import { useQueryRecommendIngredientProductsQuery } from '@/containers/hooks/product-detail/useQueryProductDetailIngredientProducts'
import { GraphQLProductType, transformProductTypeDefinedByFrontend } from '@/utils/product/product-card.util'
import { decodeId, encodeId } from '@/utils/utilApi'

interface ProductDetailIngredientsListProps {
  productId: number
  productIngredient: ProductIngredientScheme[]
  discountRate: number
  isRealSoldOut: boolean
  productName: string
  price: number
  salePrice: number
  reviewScore: number
  reviewCount: number
}

export const ProductDetailIngredientsProductList = ({
  productId,
  productIngredient,
  discountRate,
  isRealSoldOut,
  productName,
  price,
  salePrice,
  reviewScore,
  reviewCount,
}: ProductDetailIngredientsListProps) => {
  const [isShowBottomSheet, setIsShowBottomSheet] = useState(false)
  const { color } = useTheme()
  const { ref, inView } = useInView({
    triggerOnce: true,
  })
  const ingredientTypes = productIngredient
    .map((item) => item.productIngredientType)
    .filter((item) => !!item) as ProductIngredientTypeEnum[]

  const feedCategoryType = productIngredient[0].ingredientFeedCategoryType ?? IngredientFeedCategoryTypeEnum.FEED

  const { trackViewProductDetailMiddleProduct, trackClickProductDetailMiddleProduct } = useProductDetailEventTracker()
  const [selectIngredientType, setSelectIngredientType] = useState(ingredientTypes[0])
  const [chipIndex, setChipIndex] = useState(0)
  const { data, isLoading } = useQueryRecommendIngredientProductsQuery({
    productId: encodeId('ProductType', productId),
    feedIngredientType: selectIngredientType,
  })

  const INGREDIENT_PRODUCT = {
    listType: 'ingredient',
  }

  useEffect(() => {
    if (inView) {
      trackViewProductDetailMiddleProduct({
        listType: INGREDIENT_PRODUCT.listType,
        isDirectResult: true,
        sectionName: `이 상품과 비슷한 ${INGREDIENT_TEXT[selectIngredientType]} ${FEED_TEXT[feedCategoryType]}`,
        chipName: INGREDIENT_TEXT[selectIngredientType],
        categoryTypeName: FEED_TEXT[feedCategoryType],
        chipListIndex: 0,
        totalChipCount: ingredientTypes.length,
      })
    }
  }, [inView])

  const handleClickInfo = () => {
    setIsShowBottomSheet(true)
    document.body.style.overflow = 'hidden'
  }

  const handleClickChip = ({ ingredientType, index }: { ingredientType: IngredientText; index: number }) => {
    setSelectIngredientType(ingredientType)
    setChipIndex(index)

    trackViewProductDetailMiddleProduct({
      listType: INGREDIENT_PRODUCT.listType,
      isDirectResult: false,
      sectionName: `이 상품과 비슷한 ${INGREDIENT_TEXT[ingredientType]} ${FEED_TEXT[feedCategoryType]}`,
      chipName: INGREDIENT_TEXT[ingredientType],
      categoryTypeName: FEED_TEXT[feedCategoryType],
      chipListIndex: index,
      totalChipCount: ingredientTypes.length,
    })
  }

  const handleClickProductCard = ({ item, listIndex }: { item: GraphQLProductType; listIndex: number }) => {
    trackClickProductDetailMiddleProduct({
      discountRate,
      productId,
      isRealSoldOut,
      productName,
      price,
      salePrice,
      reviewScore,
      reviewCount,
      listType: INGREDIENT_PRODUCT.listType,
      listIndex,
      sectionName: `이 상품과 비슷한 ${INGREDIENT_TEXT[selectIngredientType]} ${FEED_TEXT[feedCategoryType]}`,
      clickedProduct: {
        brandName: item.brand.name,
        brandId: +decodeId(item.brand.id).id,
        discountRate: item.discountRate,
        productId: +decodeId(item.id).id,
        isRealSoldOut: item.isRealSoldOut,
        productName: item.name,
        price: item.price,
        salePrice: item.customerPrice,
        reviewScore: item.averageReviewScore,
        reviewCount: item.reviewCount,
        ingredient: {
          chipName: INGREDIENT_TEXT[selectIngredientType],
          categoryTypeName: FEED_TEXT[feedCategoryType],
          chipListIndex: chipIndex,
          totalChipCount: ingredientTypes.length,
        },
      },
    })
  }

  if (!feedCategoryType) {
    return null
  }

  return (
    <ProductDetailIngredientsProductListWrapper className="product-detail-ingredients-product-list" ref={ref}>
      <div className="product-detail-ingredients-product-list__header">
        <div className="product-detail-ingredients-product-list__title">
          <Typo variant={TypoVariant.Heading6Bold} color={color.grayWhite}>
            이 상품과 비슷한
          </Typo>
          <Typo variant={TypoVariant.Heading6Bold} color={color.orange200}>
            {INGREDIENT_TEXT[selectIngredientType]}
          </Typo>
          <Typo variant={TypoVariant.Heading6Bold} color={color.grayWhite}>
            {FEED_TEXT[feedCategoryType]}
          </Typo>
        </div>
        <button className="product-detail-ingredients-product-list__info-icon" type="button" onClick={handleClickInfo}>
          <FlatIcon type={IconNameEnum.IcTip} color={color.gray500} size="2rem" />
        </button>
      </div>
      {ingredientTypes.length > 1 && (
        <ul className="product-detail-ingredients-product-list__chip-list">
          {ingredientTypes.map((ingredientType, index) => {
            const isActive = ingredientType === selectIngredientType
            return (
              <li key={index} className="product-detail-ingredients-product-list__chip-item">
                <button
                  type="button"
                  className={`product-detail-ingredients-product-list__chip ${
                    isActive && 'product-detail-ingredients-product-list__chip--active'
                  }`}
                  onClick={() =>
                    handleClickChip({
                      ingredientType,
                      index,
                    })
                  }
                >
                  <Typo variant={TypoVariant.Body3Bold} color={isActive ? color.blue900 : color.grayWhite}>
                    {INGREDIENT_TEXT[ingredientType]}
                  </Typo>
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <div className="product-detail-ingredients-product-list__content">
        <ScrollContainer hideRightButton={data.length < 3} buttonPositionY={30}>
          <div className="product-detail-ingredients-product-list__list">
            {isLoading ? (
              <SkeletonProduct length={3} size={ProductThumbnailSizeEnum.Card136} />
            ) : (
              data.map((product, index) => {
                if (!product) {
                  return null
                }
                const productData = transformProductTypeDefinedByFrontend(product)
                return (
                  <div className="product-detail-ingredients-product-list__item" key={productData.id}>
                    <NewProductCard
                      product={productData}
                      size={PRODUCT_CARD_SIZES.lg}
                      onClickProductCard={() => handleClickProductCard({ item: product, listIndex: index })}
                    />
                  </div>
                )
              })
            )}
          </div>
        </ScrollContainer>
      </div>
      <BottomSheetModal
        title=""
        titleNoBorder
        visible={isShowBottomSheet}
        onCancel={() => {
          setIsShowBottomSheet(false)
          document.body.style.overflow = 'unset'
        }}
        onClose={() => {
          setIsShowBottomSheet(false)
          document.body.style.overflow = 'unset'
        }}
      >
        <ProductDetailIngredientsInfo selectIngredientType={selectIngredientType} />
      </BottomSheetModal>
    </ProductDetailIngredientsProductListWrapper>
  )
}

const ProductDetailIngredientsProductListWrapper = styled.div`
  padding: 1.2rem 0 2.4rem;
  &::before {
    content: '';
    width: 100%;
    height: 16.8rem;
    background-color: ${({
      theme: {
        color: { blue900 },
      },
    }) => blue900};
    position: absolute;
    top: 0;
    left: 0;
  }
  position: relative;
  border-bottom: 0.8rem solid
    ${({
      theme: {
        color: { gray70 },
      },
    }) => gray70};
  .product-detail-ingredients-product-list {
    &__header {
      position: relative;
      padding: 1.6rem;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.8rem;
      justify-content: space-between;
    }
    &__title {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.4rem;
    }
    &__info-icon {
      cursor: pointer;
      background: transparent;
      border: none;
    }
    &__chip-list {
      position: relative;
      padding: 0 1.6rem;
      margin-bottom: 1.6rem;
      display: flex;
      flex-direction: row;
      gap: 0.8rem;
      align-items: center;
    }
    &__chip {
      cursor: pointer;
      outline: none;
      border: none;
      padding: 0.6rem 1rem;
      border-radius: 2rem;
      border: 1px solid
        ${({
          theme: {
            color: { gray200 },
          },
        }) => gray200};
      background: transparent;
    }
    &__chip--active {
      background: ${({
        theme: {
          color: { grayWhite },
        },
      }) => grayWhite};
      border: 1px solid
        ${({
          theme: {
            color: { grayWhite },
          },
        }) => grayWhite};
    }
    &__list {
      display: grid;
      grid-auto-flow: column;
      grid-template-columns: repeat(3, 1fr);
      grid-column-gap: 0.8rem;
      & > *:first-child {
        padding-left: 1.6rem;
      }
      & > *:last-child {
        padding-right: 1.6rem;
      }
    }
  }
`
